import React, { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import { Save, Image as ImageIcon, Palette } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminSettings = () => {
  const { getAuthConfig } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    // Branding
    logo_light_url: '',
    logo_dark_url: '',
    favicon_url: '',
    og_image_url: '',
    
    // SEO
    site_meta_title: 'Rénovation de Salle de Bains - Suisse Romande | Service Premium',
    site_meta_description: 'Service premium de rénovation de salle de bains en Suisse romande. Solution clé en main avec finitions impeccables. Devis gratuit sous 48h.',
    
    // Colors
    primary_color: '#336DBF',
    secondary_color: '#2B2B2B',
    button_color: '#336DBF',
    
    // Tracking
    facebook_pixel_id: '',
    google_analytics_id: '',
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/admin/settings`,
        getAuthConfig()
      );
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast.error('Erreur lors du chargement des paramètres');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${BACKEND_URL}/api/admin/settings`,
        settings,
        getAuthConfig()
      );
      toast.success('Paramètres enregistrés avec succès!');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setSettings({ ...settings, [field]: value });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#336DBF]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-settings-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-[#2B2B2B]">Paramètres du Site</h2>
          <p className="text-slate-600 mt-1">Configuration globale du site web</p>
        </div>
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-[#336DBF] hover:bg-[#2F62AA] text-white"
          data-testid="save-settings-btn"
        >
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Enregistrement...' : 'Enregistrer'}
        </Button>
      </div>

      {/* Branding Section */}
      <Card className="border-slate-200/70">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ImageIcon className="w-5 h-5" />
            Branding & Images
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Logo Fond Clair */}
          <div>
            <Label htmlFor="logo-light">Logo (Fond Clair)</Label>
            <Input
              id="logo-light"
              value={settings.logo_light_url || ''}
              onChange={(e) => handleChange('logo_light_url', e.target.value)}
              placeholder="https://votre-site.com/logo-light.png"
              className="mt-2"
            />
            <p className="text-xs text-slate-500 mt-1">
              Logo affiché sur fond clair (header, footer)
            </p>
            {settings.logo_light_url && (
              <div className="mt-3 p-4 bg-white border border-slate-200 rounded-lg inline-block">
                <img src={settings.logo_light_url} alt="Logo clair" className="h-12 object-contain" onError={(e) => e.target.style.display = 'none'} />
              </div>
            )}
          </div>

          {/* Logo Fond Sombre */}
          <div>
            <Label htmlFor="logo-dark">Logo (Fond Sombre)</Label>
            <Input
              id="logo-dark"
              value={settings.logo_dark_url || ''}
              onChange={(e) => handleChange('logo_dark_url', e.target.value)}
              placeholder="https://votre-site.com/logo-dark.png"
              className="mt-2"
            />
            <p className="text-xs text-slate-500 mt-1">
              Logo affiché sur fond sombre (version inversée)
            </p>
            {settings.logo_dark_url && (
              <div className="mt-3 p-4 bg-[#2B2B2B] border border-slate-700 rounded-lg inline-block">
                <img src={settings.logo_dark_url} alt="Logo sombre" className="h-12 object-contain" onError={(e) => e.target.style.display = 'none'} />
              </div>
            )}
          </div>

          {/* Favicon */}
          <div>
            <Label htmlFor="favicon">Favicon</Label>
            <Input
              id="favicon"
              value={settings.favicon_url || ''}
              onChange={(e) => handleChange('favicon_url', e.target.value)}
              placeholder="https://votre-site.com/favicon.ico"
              className="mt-2"
            />
            <p className="text-xs text-slate-500 mt-1">
              Icône affichée dans l'onglet du navigateur (32x32px ou 16x16px)
            </p>
            {settings.favicon_url && (
              <div className="mt-3">
                <img src={settings.favicon_url} alt="Favicon" className="w-8 h-8 border border-slate-200 rounded" onError={(e) => e.target.style.display = 'none'} />
              </div>
            )}
          </div>

          {/* Open Graph Image */}
          <div>
            <Label htmlFor="og-image">Image Réseaux Sociaux (Open Graph)</Label>
            <Input
              id="og-image"
              value={settings.og_image_url || ''}
              onChange={(e) => handleChange('og_image_url', e.target.value)}
              placeholder="https://votre-site.com/og-image.jpg"
              className="mt-2"
            />
            <p className="text-xs text-slate-500 mt-1">
              Image affichée lors du partage sur Facebook, Twitter, LinkedIn (1200x630px recommandé)
            </p>
            {settings.og_image_url && (
              <div className="mt-3">
                <img src={settings.og_image_url} alt="OG Image" className="max-w-md h-auto border border-slate-200 rounded-lg" onError={(e) => e.target.style.display = 'none'} />
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* SEO Section */}
      <Card className="border-slate-200/70">
        <CardHeader>
          <CardTitle>SEO & Métadonnées</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="meta-title">Meta Title (Titre du Site)</Label>
            <Input
              id="meta-title"
              value={settings.site_meta_title || ''}
              onChange={(e) => handleChange('site_meta_title', e.target.value)}
              placeholder="Rénovation de Salle de Bains - Suisse Romande"
              className="mt-2"
              maxLength={60}
            />
            <p className="text-xs text-slate-500 mt-1">
              Titre affiché dans les résultats Google (max 60 caractères) - {settings.site_meta_title?.length || 0}/60
            </p>
          </div>

          <div>
            <Label htmlFor="meta-description">Meta Description</Label>
            <Textarea
              id="meta-description"
              value={settings.site_meta_description || ''}
              onChange={(e) => handleChange('site_meta_description', e.target.value)}
              placeholder="Service premium de rénovation de salle de bains..."
              className="mt-2"
              rows={3}
              maxLength={160}
            />
            <p className="text-xs text-slate-500 mt-1">
              Description affichée dans les résultats Google (max 160 caractères) - {settings.site_meta_description?.length || 0}/160
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Colors Section */}
      <Card className="border-slate-200/70">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Palette className="w-5 h-5" />
            Couleurs du Site
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid md:grid-cols-3 gap-6">
            {/* Primary Color */}
            <div>
              <Label htmlFor="primary-color">Couleur Primaire</Label>
              <div className="flex gap-2 mt-2">
                <Input
                  id="primary-color"
                  type="color"
                  value={settings.primary_color || '#336DBF'}
                  onChange={(e) => handleChange('primary_color', e.target.value)}
                  className="w-20 h-10 cursor-pointer"
                />
                <Input
                  value={settings.primary_color || '#336DBF'}
                  onChange={(e) => handleChange('primary_color', e.target.value)}
                  placeholder="#336DBF"
                  className="flex-1"
                />
              </div>
              <p className="text-xs text-slate-500 mt-1">Couleur principale du site (liens, accents)</p>
            </div>

            {/* Secondary Color */}
            <div>
              <Label htmlFor="secondary-color">Couleur Secondaire</Label>
              <div className="flex gap-2 mt-2">
                <Input
                  id="secondary-color"
                  type="color"
                  value={settings.secondary_color || '#2B2B2B'}
                  onChange={(e) => handleChange('secondary_color', e.target.value)}
                  className="w-20 h-10 cursor-pointer"
                />
                <Input
                  value={settings.secondary_color || '#2B2B2B'}
                  onChange={(e) => handleChange('secondary_color', e.target.value)}
                  placeholder="#2B2B2B"
                  className="flex-1"
                />
              </div>
              <p className="text-xs text-slate-500 mt-1">Couleur secondaire (titres, footer)</p>
            </div>

            {/* Button Color */}
            <div>
              <Label htmlFor="button-color">Couleur des Boutons</Label>
              <div className="flex gap-2 mt-2">
                <Input
                  id="button-color"
                  type="color"
                  value={settings.button_color || '#336DBF'}
                  onChange={(e) => handleChange('button_color', e.target.value)}
                  className="w-20 h-10 cursor-pointer"
                />
                <Input
                  value={settings.button_color || '#336DBF'}
                  onChange={(e) => handleChange('button_color', e.target.value)}
                  placeholder="#336DBF"
                  className="flex-1"
                />
              </div>
              <p className="text-xs text-slate-500 mt-1">Couleur des boutons CTA</p>
            </div>
          </div>

          {/* Preview */}
          <div className="p-6 bg-slate-50 rounded-lg border border-slate-200">
            <p className="text-sm font-medium text-slate-700 mb-4">Aperçu des couleurs:</p>
            <div className="flex gap-4 flex-wrap">
              <Button style={{ backgroundColor: settings.button_color }} className="text-white">
                Bouton CTA
              </Button>
              <div className="px-4 py-2 rounded" style={{ backgroundColor: settings.primary_color, color: 'white' }}>
                Couleur Primaire
              </div>
              <div className="px-4 py-2 rounded" style={{ backgroundColor: settings.secondary_color, color: 'white' }}>
                Couleur Secondaire
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tracking Section */}
      <Card className="border-slate-200/70">
        <CardHeader>
          <CardTitle>Tracking & Analytics</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="facebook-pixel">Facebook Pixel ID</Label>
            <Input
              id="facebook-pixel"
              value={settings.facebook_pixel_id || ''}
              onChange={(e) => handleChange('facebook_pixel_id', e.target.value)}
              placeholder="123456789012345"
              className="mt-2"
            />
            <p className="text-xs text-slate-500 mt-1">
              ID du Facebook Pixel pour tracking des conversions
            </p>
          </div>

          <div>
            <Label htmlFor="google-analytics">Google Analytics ID</Label>
            <Input
              id="google-analytics"
              value={settings.google_analytics_id || ''}
              onChange={(e) => handleChange('google_analytics_id', e.target.value)}
              placeholder="G-XXXXXXXXXX ou UA-XXXXXXXXX-X"
              className="mt-2"
            />
            <p className="text-xs text-slate-500 mt-1">
              ID de Google Analytics pour tracking du trafic
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Save Button (bottom) */}
      <div className="flex justify-end">
        <Button
          onClick={handleSave}
          disabled={saving}
          size="lg"
          className="bg-[#336DBF] hover:bg-[#2F62AA] text-white px-8"
        >
          <Save className="w-5 h-5 mr-2" />
          {saving ? 'Enregistrement...' : 'Enregistrer tous les paramètres'}
        </Button>
      </div>
    </div>
  );
};

export default AdminSettings;